import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { FullScreenLoading, NUGGET_VARIANTS } from '@backrs_ui/common-ui';

import { APP_ROUTE } from '@backrs_ui/grow-ui/src/appRoutes.constants';
import { usePageTitle } from '@backrs_ui/grow-ui/src/hooks/usePageTitle';
import { getBookmarkIdForNugget } from '@backrs_ui/grow-ui/src/pages/Bookmarks/pages/bookmarks.util';
import {
  usePostBookmarkMutation,
  useDeleteBookmarkMutation,
  useGetBookmarksQuery,
} from '@backrs_ui/grow-ui/src/redux/bookmarks/bookmarks.api';
import { useGetCollegeByIdQuery } from '@backrs_ui/grow-ui/src/redux/college/college.api';
import { PagePrompt } from '@backrs_ui/grow-ui/src/redux/pagePrompts/pagePrompts';
import { useRegisterPagePrompts } from '@backrs_ui/grow-ui/src/redux/pagePrompts/useRegisterPagePrompts';

import { CollegePage } from './CollegePage';
import { useAgentContext } from './CollegePageContainer.agentContext';
import { SEARCH_PAGE_CATEGORIES } from '../Search/search.types';

const COLLEGE_PROMPTS: readonly PagePrompt[] = [
  { promptButtonId: 'college_am_i_a_good_fit', title: 'Is this school a good fit for me?' },
  { promptButtonId: 'college_what_do_students_say', title: 'What do students say?' },
  { promptButtonId: 'college_campus_life', title: 'Campus life' },
  { promptButtonId: 'college_example_profiles', title: 'Example extracurricular profiles' },
  { promptButtonId: 'college_how_can_i_learn_more', title: 'How can I learn more?' },
  { promptButtonId: 'college_improve_my_odds', title: 'Improve my odds of admission' },
];

export const CollegePageContainer = () => {
  const { universityId } = useParams();

  const navigate = useNavigate();
  const [postBookmark] = usePostBookmarkMutation();
  const [deleteBookmark] = useDeleteBookmarkMutation();
  const { data: bookmarks } = useGetBookmarksQuery(undefined);

  const {
    data: foundCollege,
    isLoading,
    error,
  } = useGetCollegeByIdQuery(universityId || '', {
    skip: !universityId,
  });

  const collegeBookmarkId = getBookmarkIdForNugget(
    bookmarks,
    foundCollege?.id,
    NUGGET_VARIANTS.INSTITUTION,
  );

  useRegisterPagePrompts(foundCollege ? COLLEGE_PROMPTS : null);

  useEffect(() => {
    if (!universityId) {
      navigate(`${APP_ROUTE.SEARCH}/${SEARCH_PAGE_CATEGORIES.COLLEGES}`);
    }
  }, [navigate, universityId]);

  usePageTitle(foundCollege?.school_name);

  useAgentContext(foundCollege);

  if (!universityId || isLoading || !foundCollege || error) {
    return <FullScreenLoading />;
  }

  const handleBookmark = async () => {
    if (collegeBookmarkId) {
      await deleteBookmark(collegeBookmarkId);

      return;
    }

    await postBookmark({ type: 'college', itemId: foundCollege.id });
  };

  return (
    <CollegePage
      college={foundCollege}
      handleBookmark={handleBookmark}
      hasBookmarked={!!collegeBookmarkId}
    />
  );
};
